import { BlankSlate } from "@msuite/ui/blank-slate";
import { Button } from "@msuite/ui/button";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { type QueryKey, useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { AUDIT_IDENTIFIER_KEYS, AUDIT_TABLES } from "@msuite/utils/schemas";
import type { AuditTable, Tables } from "@msuite/utils/types";
import { AuditCard } from "@/components/cards/audit-card";
import { client } from "@/hono";

/**
 * The endpoint accepts only the audit tables and their identifier columns, so the props carry
 * the same two closed sets rather than every table name. A call site that names something else
 * fails to compile instead of failing at request time.
 */
type AuditTableName = (typeof AUDIT_TABLES)[number];
type AuditIdentifierKey = (typeof AUDIT_IDENTIFIER_KEYS)[number];

/** Props Interface */
interface AuditModalProps<T extends AuditTableName> extends ModalProps {
	auditTable: T;
	queryKey: QueryKey;
	identifier: {
		key: AuditIdentifierKey;
		value: string;
	};
}

export function AuditModal<T extends AuditTableName>({
	isOpen,
	onClose,
	queryKey,
	auditTable,
	identifier,
}: AuditModalProps<T>) {
	/** Hooks */
	const { t } = useTranslation();

	/** Query */
	const { data } = useQuery({
		queryKey,
		queryFn: async () => {
			const response = await client.audit.index.$get({
				query: {
					auditTable,
					identifierKey: identifier.key,
					identifierValue: identifier.value,
				},
			});
			const data = await responseHandler(response as any);
			return data.audits as AuditTable[];
		},
	});

	const updateData = (data as unknown as Tables<AuditTable>[])?.filter((auditItem) => {
		const type = "operation" in auditItem ? auditItem.operation : "UPDATE";
		return type === "UPDATE";
	});

	/** Render */
	return (
		<Modal
			title={t("general.history")}
			isOpen={isOpen}
			onClose={onClose}
			width="800px"
		>
			<Modal.Body>
				{!updateData?.length && <BlankSlate />}
				{updateData?.map((audit) => (
					<AuditCard
						key={audit.id}
						audit={audit as any}
					/>
				))}
			</Modal.Body>
			<Modal.Footer>
				<Button
					variant="primary"
					onClick={onClose}
				>
					{t("general.done")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
